<template>
	<div :class="dynamicClass" :id="'scrolltarget-' + fieldName" v-if="!isHidden">
		<label v-if="label" :for="fieldId">{{ label }}</label>
		<div class="c-single-input__field" :style="calculatedFieldSizeStyle">
			<input
				:id="fieldId"
				:type="clientSideInputType"
				:name="name"
				:value="localModelValue"
				:placeholder="placeholderText"
				:tabindex="0"
				:required="required"
				:maxlength="maxLength"
				:readonly="readOnly"
				:disabled="readOnly"
				:min="min"
				:max="max"
				:inputmode="inputMode"
				:aria-invalid="hasError(fieldName) || null"
				:aria-errormessage="'error_' + fieldId"
				:data-field-name="fieldName"
				@focus="onFocus"
				@blur="onBlur"
				@input="onInput"
				@keyup="onKeyUp"
			/>
			<a class="c-single-input__link" v-if="hasLink" :href="linkUrl"><DsText text-type="body-md" text-style="medium" html-element="span">{{ linkText }}</DsText></a>
		</div>
		<div :id="'error_' + fieldId" :data-tracking-name="trackingName">
			<span
				class="error show-error validation-error"
				v-if="hasErrorType(fieldName, 'requiredError')"
				>{{ requiredErrorText }}</span
			>
			<span
				class="error show-error max-length-error"
				v-if="hasErrorType(fieldName, 'maxLengthError')"
				>{{ maxLengthErrorText }}</span
			>
			<span
				class="error show-error format-error"
				v-if="hasErrorType(fieldName, 'formatError')"
				>{{ formatErrorText }}</span
			>
			<span
				class="error show-error api-validation-error"
				v-if="hasErrorType(fieldName, 'apiValidationError')"
				>{{ apiValidationError }}</span
			>
		</div>
	</div>
</template>

<script>
import store from 'shared/store';
import { trackingFieldName, validateFieldState } from 'multisite/services/checkoutHelperService.js';
import { addressTypes } from 'shared/settings/constants';
import { DsText } from "@coloplast/design-system";

export default {
	name: 'single-input-field',
	store,
	components: {
		DsText,
	},
	props: {
		modelValue: [String, Number],
		submitted: Boolean,
		fieldId: {
			type: String,
			default: null,
		},
		label: {
			type: String,
			default: null,
		},
		name: {
			type: String,
			default: null,
		},
		fieldName: {
			type: String,
			default: null,
		},
		required: {
			type: Boolean,
			default: false,
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		linkText: {
			type: String,
			default: null,
		},
		linkUrl: {
			type: String,
			default: null,
		},
		maxLength: {
			type: Number,
			default: null,
		},
		maxLengthErrorText: {
			type: String,
			default: '',
		},
		max: {
			type: String,
			default: null,
		},
		min: {
			type: String,
			default: null,
		},
		requiredErrorText: {
			type: String,
			default: '',
		},
		formatErrorText: {
			type: String,
			default: '',
		},
		placeholderText: {
			type: String,
			default: '',
		},
		clientSideInputType: {
			type: String,
			default: 'text',
		},
		inputMode: {
			type: String,
			default: null,
		},
		validatePattern: {
			default: null,
		},
		errors: {
			type: Object,
			default: () => ({}),
		},
		/** @type {function(fieldName: string, state: Object)} */
		updateFieldError: {
			type: Function,
		},
		availableForAlternativeAddresses: {
			type: Boolean,
			default: false,
		},
		addressType: {
			type: String,
			default: 'shippingAddress',
		},
	},
	emits: ['update:modelValue', 'blur', 'focus', 'keyup', 'validate'],
	data() {
		return {
			isDirty: false,
			hasBlur: false,
			trackingName: trackingFieldName(this.fieldName),
			localModelValue: this.modelValue
		};
	},
	computed: {
		apiValidationError() {
			return this.errors[this.fieldName]
				? this.errors[this.fieldName].apiValidationError
				: '';
		},
		numCharacters() {
			if (this.localModelValue) {
				return this.localModelValue.length;
			}
			return 0;
		},
		calculatedFieldSizeStyle() {
			return `--field-size: ${this.numCharacters}ch`;
		},
		dynamicClass() {
			if (this.hasLink) {
				return "c-single-input c-single-input--has-link";
			}
			return "c-single-input";
		},
		hasLink() {
			return this.linkText !== null && this.linkText?.length > 0 && this.linkUrl !== null && this.linkUrl?.length > 0;
		},
		isHidden() {
			return (
				this.addressType !== addressTypes.SHIPPING_ADDRESS &&
				!this.availableForAlternativeAddresses
			);
		},
		isDirtyBlur() {
			return this.hasBlur && this.isDirty;
		},
	},
	watch: {
		modelValue(newVal) {
			this.localModelValue = newVal;
			const shouldClearApiError = true;
			this.validateField(shouldClearApiError);
		},
		apiValidationError() {
			this.validateField();
		},
	},
	mounted() {
		this.validateField();
	},
	methods: {
		hasErrorType(item, type) {
			return (this.isDirtyBlur || this.submitted) && this.errors[item] ? this.errors[item][type] : false;
		},
		hasError(item) {
			return this.isDirtyBlur || this.submitted ? this.errors[item] && !this.errors[item].isValid : false;
		},
		validateField(shouldClearApiError) {
			const validationResult = validateFieldState(this.localModelValue, {
				required: this.required,
				maxLength: this.maxLength,
				validatePattern: this.validatePattern,
				apiValidationError: shouldClearApiError ? '' : this.apiValidationError,
			});
			if (this.updateFieldError) {
				this.updateFieldError(this.fieldName, validationResult);
			}
			this.$emit('validate', {
				key: this.fieldName,
				value: validationResult,
			});
		},
		onBlur(event) {
			this.hasBlur = true;
			this.$emit('blur', event);
		},
		onFocus(event) {
			this.hasBlur = false;
			this.$emit('focus', event);
		},
		onInput(event) {
			this.isDirty = true;
			this.localModelValue = event.target.value;
			this.$emit('update:modelValue', event.target.value);
		},
		onKeyUp(event) {
			this.$emit('keyup', event);
		},
	},
};
</script>

<style lang="scss">
@import "multisite/styles/settings/settings";
@import "multisite/styles/tools/tools";
.c-single-input {
	&--has-link &__field {
		display: flex;
		gap: ds-space(4);

		input[readonly] {
			width: var(--field-size);

			@supports(field-sizing: content) {
				field-sizing: content;
				width: auto;
			}
		}
	}

	&__link {
		text-decoration: none;
		transform: translateY(-.2em);
	}
}
</style>
